import * as THREE from 'three';
import type { World } from '../../shared/types.js';
import { resample } from '../../shared/geom.js';
import { Heightfield } from '../../shared/heightfield.js';
import type { Skyline } from './outroCamera.js';

/**
 * The establishing shot, flown while everyone is saying they are ready: a drone that takes
 * off behind the start line and flies the route, gate by gate, to the finish.
 *
 * It replaced an orbit around the route's bounding box (`OrbitCamera`, which still circles
 * the city behind the menu). Framing a whole route from one point means standing back by
 * the route's length, and for anything longer than an old town that put the camera half a
 * kilometre up: the city a strip along the bottom of the frame, fog over most of the rest,
 * and above it the edge of the generated world hanging in the sky. Following the route
 * instead keeps the camera low however long the race is, so the frame is always city, and
 * it answers the question the shot exists for — where does this race go — in the order
 * the driver will need the answer. DECISIONS.md §68.
 *
 * The whole flight is known the moment the world is, so it is worked out once, as profiles
 * sampled every few metres along the route — position, height, heading, pitch, haze — and
 * each frame only reads them back. That is what lets the shot be smoothed with
 * *hindsight*: the camera starts climbing before the tower rather than when it notices it,
 * and the haze starts thickening before the world's edge comes into frame rather than
 * after.
 */

/** Metres between profile samples. Fine enough that interpolating them never shows. */
const STEP = 4;

/**
 * Vertical field of view. Narrower than the chase camera's 55°: from a hundred metres up a
 * wide lens spends the top of the frame on distance, and distance is where the haze and
 * the world's edge are.
 */
export const FLYOVER_FOV = 50;

/**
 * Degrees below the horizon the camera looks along the way.
 *
 * Shallow enough that buildings read as buildings rather than roof plans and the city runs
 * off into haze at the top of the frame, which is most of what makes it look like a place
 * rather than a map. Steep enough that the top edge of the frame is still 8° below the
 * horizon, so there is no sky in it — a band of sky over a city that stops is the shot
 * this replaced.
 */
const BASE_PITCH = 33;
/** The most the frame-keeping is allowed to tip it, a little short of straight down. */
const MAX_PITCH = 80;

/**
 * Seconds to fly a route, scaled by its length and clamped.
 *
 * The floor on staging is six seconds and nobody knows the ceiling, so the flight is not
 * fitted to either: it is paced so that a route reads as a journey — a quarter of a minute
 * for a few hundred metres, half a minute for a cross-town run — and what happens when
 * staging outlasts it is `HOLD_SECONDS` and another pass.
 */
const FLIGHT_SECONDS = { base: 10, perKm: 8, min: 14, max: 30 } as const;
const EASE_IN_SECONDS = 1.5;
const EASE_OUT_SECONDS = 4;

/**
 * Cruising height per metre-a-second of speed.
 *
 * Ground speed is not what smears a shot, angular speed is — so a faster flight flies
 * higher, keeping the city sliding under the frame at about the same rate whatever the
 * route length. Clamped below so a short route is still an overview, and above so a long
 * one stays a city rather than a map.
 */
const HEIGHT_PER_SPEED = 1.4;
const MIN_HEIGHT = 70;
const MAX_HEIGHT = 180;

/** Metres of air above the tallest roof near the camera. Kept as a profile — see `planFlight`. */
const ROOF_CLEARANCE = 35;
/** How near, in metres, counts — see `roofNear`. Added to the skyline's own ±48 m. */
const ROOF_REACH = 80;

/** How far behind the start line the drone takes off, as a fraction of its height. */
const LEAD_IN = 0.8;
/**
 * Where it comes to rest short of the finish, as a fraction of its height. Close enough
 * that the last look is down onto the gantry at about 65°, not across it.
 */
const STAND_OFF = 0.45;

/**
 * Corners rounded off the driving line before the drone flies it — at least this many
 * metres, more for a fast flight. A drone does not brake.
 */
const SMOOTHING_METRES = 36;

/**
 * The shot's own haze: where it is total, in multiples of the camera's height above the
 * ground, and where it starts, as a fraction of that.
 *
 * The weather's fog is tuned for a driver — clear for a few hundred metres down the street.
 * From a hundred metres up that leaves the top of the frame sharp out to wherever the
 * region happens to stop. Scaled to the height instead, the city runs off into haze towards
 * the top of the frame the way a real aerial shot does, and the haze is what hides the
 * region's edge: it is pulled in further, a little at a time, wherever the route runs near
 * one (`frameFlight`). Only ever closer than the weather's: a foggy day stays foggy.
 */
const FOG_FAR = 8;
const FOG_NEAR_RATIO = 0.33;
/**
 * The haze may close in until it is this many times the distance to the middle of the
 * frame and no further — past that the subject itself is going grey, and the camera tips
 * down instead.
 */
const FOG_FLOOR = 2.3;
/**
 * How deep into the haze ground outside the region has to be before it may be in frame:
 * about 90% fogged, past which an empty field and the fog colour are the same colour.
 */
const HIDDEN_AT = 0.88;

/** After arriving over the finish: a beat to take it in, then a dip to black and go again. */
const HOLD_SECONDS = 1.6;
const FADE_SECONDS = 0.7;

/**
 * Points on the border of the frame that are cast onto the ground, as (x, y) in [-1, 1].
 * The corners alone are not enough: a top edge can cross the region's boundary much
 * nearer than either of its ends.
 */
const BORDER: ReadonlyArray<readonly [number, number]> = [
  [-1, 1], [-0.5, 1], [0, 1], [0.5, 1], [1, 1],
  [-1, 0.5], [1, 0.5], [-1, 0], [1, 0], [-1, -0.5], [1, -0.5],
  [-1, -1], [0, -1], [1, -1],
];

export interface Flight {
  x: Float32Array;
  z: Float32Array;
  /** Camera height, absolute — the ground's own height is in `ground`. */
  y: Float32Array;
  ground: Float32Array;
  yaw: Float32Array;
  /** Pitch the route asks for, before the frame-keeping has had its say. */
  basePitch: Float32Array;
  /** Pitch actually flown, for the aspect it was last framed for. */
  pitch: Float32Array;
  /** Where the haze is total, for the aspect it was last framed for. */
  fogFar: Float32Array;
  /** Sample index each checkpoint lies under, in race order. */
  gates: number[];
  /** Index of the last sample the camera flies to. */
  last: number;
  seconds: number;
  /** Cruising speed, metres a second. */
  speed: number;
  cruise: number;
  /** Smoothing window for the framing profiles, in samples — about a second of flight. */
  window: number;
  extentX: number;
  extentZ: number;
}

export class FlyoverCamera {
  private flight: Flight | null = null;
  private framedFor = 0;
  private active = false;
  /** Seconds since `reset`, across passes. */
  private elapsed = 0;
  /** Seconds into the current pass. */
  private clock = 0;
  private passes = 0;
  private still = false;
  private readonly haze = { near: 0, far: 0 };

  setWorld(world: World | null, skyline: Skyline | null): void {
    this.flight = world ? planFlight(world, skyline) : null;
    this.framedFor = 0;
  }

  /** Begins the shot from the start line. */
  reset(): void {
    this.active = true;
    this.elapsed = 0;
    this.clock = 0;
    this.passes = 0;
    // Somebody who has asked the browser for less motion gets the opening frame and keeps
    // it. Unlike the menu's backdrop there is a still worth showing here: the grid and the
    // first stretch of the route, which is most of what the flight is for.
    this.still = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  }

  stop(): void {
    this.active = false;
  }

  get running(): boolean {
    return this.active;
  }

  /** The haze for the current frame, or null with no shot to hold it for. */
  get fog(): { near: number; far: number } | null {
    return this.flight && this.active && this.haze.far > 0 ? this.haze : null;
  }

  /**
   * How many checkpoints the drone has flown over on this pass — the same number the race
   * uses for "next gate", so the route can light the gate it is approaching.
   */
  get gatesPassed(): number {
    const flight = this.flight;
    if (!flight) return 0;
    const at = sampleAt(flight, this.clock);
    let passed = 0;
    while (passed < flight.gates.length && flight.gates[passed] <= at) passed++;
    return passed;
  }

  /** 1 is the picture, 0 is black: the dip between one pass and the next. */
  get brightness(): number {
    const flight = this.flight;
    if (!flight || this.still) return 1;
    const out = flight.seconds + HOLD_SECONDS;
    if (this.clock > out) return Math.max(0, 1 - (this.clock - out) / FADE_SECONDS);
    // Only a second pass fades in. The first frame of the shot is the first thing anybody
    // sees of this city, and it should be the city.
    if (this.passes > 0 && this.clock < FADE_SECONDS) return this.clock / FADE_SECONDS;
    return 1;
  }

  update(camera: THREE.PerspectiveCamera, dt: number): void {
    if (!this.active || !this.flight) return;
    const flight = this.flight;
    this.elapsed += dt;
    if (!this.still) this.clock += dt;
    if (this.clock > flight.seconds + HOLD_SECONDS + FADE_SECONDS) {
      this.clock = 0;
      this.passes++;
    }

    if (camera.fov !== FLYOVER_FOV) {
      camera.fov = FLYOVER_FOV;
      camera.updateProjectionMatrix();
    }
    // Reframed whenever the window changes shape: a portrait phone sees far less to either
    // side and far more ahead, so what keeps the edge hidden is different.
    if (Math.abs(camera.aspect - this.framedFor) > 0.01) {
      frameFlight(flight, camera.aspect);
      this.framedFor = camera.aspect;
    }
    const at = sampleAt(flight, this.clock);
    poseAt(flight, at, camera);
    this.haze.far = sample(flight.fogFar, at, flight.last);
    this.haze.near = this.haze.far * FOG_NEAR_RATIO;
  }
}

/** Places `camera` at fractional profile index `at`. */
export function poseAt(flight: Flight, at: number, camera: THREE.Camera): void {
  camera.position.set(
    sample(flight.x, at, flight.last),
    sample(flight.y, at, flight.last),
    sample(flight.z, at, flight.last),
  );
  camera.rotation.set(-sample(flight.pitch, at, flight.last), sample(flight.yaw, at, flight.last), 0, 'YXZ');
}

/** Fractional profile index for a time into the pass: ease in, cruise, ease out, hold. */
export function sampleAt(flight: Flight, t: number): number {
  const total = flight.seconds;
  const v = flight.speed;
  let metres: number;
  if (t <= 0) metres = 0;
  else if (t < EASE_IN_SECONDS) metres = (0.5 * v * t * t) / EASE_IN_SECONDS;
  else if (t < total - EASE_OUT_SECONDS) metres = v * (EASE_IN_SECONDS / 2 + (t - EASE_IN_SECONDS));
  else if (t < total) {
    const left = total - t;
    metres = flight.last * STEP - (0.5 * v * left * left) / EASE_OUT_SECONDS;
  } else metres = flight.last * STEP;
  return THREE.MathUtils.clamp(metres / STEP, 0, flight.last);
}

function sample(values: Float32Array, at: number, last: number): number {
  const i = Math.max(0, Math.min(Math.floor(at), last - 1));
  const f = THREE.MathUtils.clamp(at - i, 0, 1);
  return values[i] + (values[i + 1] - values[i]) * f;
}

// ---------------------------------------------------------------------------
// planning the flight
// ---------------------------------------------------------------------------

/** Works out the whole flight for a world. Framed for a 16:10 window until told otherwise. */
export function planFlight(world: World, skyline: Skyline | null): Flight {
  const route = world.route;
  const ground = world.ground ? Heightfield.from(world.ground) : Heightfield.flat();

  // Seconds, speed and height first, from the route's length alone, because everything
  // after this — how far behind the line to take off, how far ahead to look — is in
  // multiples of the height.
  const length = Math.max(route.lengthMetres, 1);
  const seconds = THREE.MathUtils.clamp(
    FLIGHT_SECONDS.base + (length / 1000) * FLIGHT_SECONDS.perKm,
    FLIGHT_SECONDS.min,
    FLIGHT_SECONDS.max,
  );
  const cruise = THREE.MathUtils.clamp((length / seconds) * HEIGHT_PER_SPEED, MIN_HEIGHT, MAX_HEIGHT);

  // Take off behind the line, on the line's own heading, so the opening frame is the grid
  // with the race laid out ahead of it.
  let path = route.path.slice();
  if (path.length < 4) {
    const { x, z, dx, dz } = route.start;
    path = [x, z, x + dx * 60, z + dz * 60];
  }
  const leadIn = cruise * LEAD_IN;
  path.unshift(path[0] - route.start.dx * leadIn, path[1] - route.start.dz * leadIn);
  const dense = resample(path, STEP);
  const n = Math.max(2, dense.length / 2);
  const x = new Float32Array(n);
  const z = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    x[i] = dense[Math.min(i, dense.length / 2 - 1) * 2];
    z[i] = dense[Math.min(i, dense.length / 2 - 1) * 2 + 1];
  }
  const last = Math.max(1, n - 1 - Math.round((cruise * STAND_OFF) / STEP));
  const speed = (last * STEP) / (seconds - EASE_IN_SECONDS / 2 - EASE_OUT_SECONDS / 2);
  const window = Math.max(1, Math.round(speed / STEP));

  // Blurred rather than splined: the resampled route is already dense, and a box blur
  // keeps sample i at roughly arc-length i·STEP, which the gates and the timing rely on.
  // It also slows the drone a touch through corners, since the smoothed line is shorter.
  // Wider for a faster flight, because a corner is a matter of how long it takes, not how
  // many metres it is: half a second of a cross-town pass is fifty metres of street.
  const radius = Math.max(1, Math.round(Math.max(SMOOTHING_METRES, speed * 0.5) / STEP / 2));
  blur(x, radius, 3);
  blur(z, radius, 3);

  const y = new Float32Array(n);
  const groundAt = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    groundAt[i] = ground.heightAt(x[i], z[i]);
    // The skyline already includes the hill each building stands on.
    y[i] = Math.max(groundAt[i] + cruise, roofNear(skyline, x[i], z[i]) + ROOF_CLEARANCE);
  }
  // Height is planned with hindsight: widen every peak by a couple of seconds of flight
  // each way, then soften it no wider than that. The camera is already climbing when the
  // tower arrives, and does not drop into the gap behind one only to climb for the next.
  dilate(y, window * 2);
  blur(y, Math.max(1, Math.round((window * 2) / 3)), 3);

  // Where the camera is looking: the road ahead, as far on as keeps the pitch at
  // `BASE_PITCH`. Measured from its height *here*, so a camera that has climbed over
  // Midtown looks further down the avenue rather than straight down onto the roof it is
  // clearing.
  const target = new Int32Array(n);
  const tanBase = Math.tan(THREE.MathUtils.degToRad(BASE_PITCH));
  for (let i = 0; i < n; i++) {
    const reach = (y[i] - groundAt[i]) / tanBase;
    target[i] = Math.min(n - 1, i + Math.max(2, Math.round(reach / STEP)));
  }

  const yaw = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    // Once the finish is what it is looking at, the heading is held rather than kept
    // pointed at it. The camera is closing on a fixed point, so the bearing to it swings
    // faster the nearer it gets — on a route that bends into the finish, a spin. Held, the
    // gantry slides into the frame as the camera tips down onto it instead.
    const from = target[i] === n - 1 && i > 0 && target[i - 1] === n - 1 ? -1 : i;
    if (from < 0) {
      yaw[i] = yaw[i - 1];
      continue;
    }
    const dx = x[target[i]] - x[i];
    const dz = z[target[i]] - z[i];
    yaw[i] = Math.hypot(dx, dz) < 1 && i > 0 ? yaw[i - 1] : Math.atan2(-dx, -dz);
  }
  // Unwrapped before it is blurred, or a heading crossing ±180° averages to due south.
  for (let i = 1; i < n; i++) {
    let d = yaw[i] - yaw[i - 1];
    while (d > Math.PI) d -= Math.PI * 2;
    while (d < -Math.PI) d += Math.PI * 2;
    yaw[i] = yaw[i - 1] + d;
  }
  // A couple of seconds either way: a drone pans, it does not snap to a new street.
  blur(yaw, Math.max(Math.round(30 / STEP), Math.round(window * 0.75)), 3);

  // The pitch the route asks for. Along the way that is `BASE_PITCH` by construction; over
  // the last stretch, with the target pinned at the finish, it tips down onto the gantry —
  // which is what makes the drone's arrival read as an arrival.
  const basePitch = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const j = target[i];
    const reach = Math.max(1, Math.hypot(x[j] - x[i], z[j] - z[i]));
    basePitch[i] = THREE.MathUtils.clamp(
      Math.atan2(y[i] - groundAt[j], reach),
      THREE.MathUtils.degToRad(BASE_PITCH),
      THREE.MathUtils.degToRad(70),
    );
  }

  // Each checkpoint, found along the flight in order, so a route that doubles back past an
  // earlier gate cannot claim it early.
  const gates: number[] = [];
  let from = 0;
  for (const gate of route.checkpoints) {
    let best = from;
    let bestD = Infinity;
    for (let i = from; i < n; i++) {
      const d = (x[i] - gate.x) ** 2 + (z[i] - gate.z) ** 2;
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    gates.push(best);
    from = best;
  }

  const flight: Flight = {
    x,
    z,
    y,
    ground: groundAt,
    yaw,
    basePitch,
    pitch: basePitch.slice(),
    fogFar: new Float32Array(n),
    gates,
    last,
    seconds,
    speed,
    cruise,
    window,
    extentX: world.extent.x,
    extentZ: world.extent.z,
  };
  frameFlight(flight, 1.6);
  return flight;
}

/**
 * Keeps ground outside the region out of the picture: first with haze, then by looking down.
 *
 * Outside the bbox there is nothing — no buildings, no roads, a flat field running out to a
 * hard edge against the sky — and a route is free to start or finish a stone's throw from
 * the boundary. So for every sample the border of the frame is cast onto the ground, and
 * any of it that lands outside the region has to be deep enough in the haze not to show.
 *
 * The first answer is to pull the haze in, which reads as nothing at all: distance going
 * grey is what distance does from the air. It may only come in so far (`FOG_FLOOR`) before
 * the middle of the frame starts to fade too, and past that the camera tips down instead, a
 * degree at a time — which shortens what the top of the frame can reach, and is just as
 * invisible as a decision: the shot looks more down as it nears the edge, the way a pilot
 * would fly it.
 *
 * Both profiles are then smoothed with hindsight like the height, so the haze is already
 * thickening and the camera already tipping a second before the edge would have shown.
 */
export function frameFlight(flight: Flight, aspect: number): void {
  const tanV = Math.tan(THREE.MathUtils.degToRad(FLYOVER_FOV / 2));
  const tanH = tanV * aspect;
  const n = flight.x.length;
  const step = THREE.MathUtils.degToRad(1);
  const ceiling = THREE.MathUtils.degToRad(MAX_PITCH);
  for (let i = 0; i < n; i++) {
    const height = flight.y[i] - flight.ground[i];
    let chosen = ceiling;
    for (let p = flight.basePitch[i]; p <= ceiling; p += step) {
      const floor = (FOG_FLOOR * height) / Math.sin(p);
      if (nearestOutside(flight, i, p, tanV, tanH) * HIDDEN_AT >= floor) {
        chosen = p;
        break;
      }
    }
    flight.pitch[i] = chosen;
  }
  dilate(flight.pitch, flight.window);
  blur(flight.pitch, Math.max(1, Math.round(flight.window / 3)), 3);

  // The haze is worked out for the pitch actually flown, after its smoothing: a camera
  // tipped further down than it needed to be sees the outside nearer than it would have.
  for (let i = 0; i < n; i++) {
    // Clear air scales with the height actually flown, not the cruise: a camera that has
    // climbed over Midtown is looking twice as far, and a haze set for the cruise would
    // grey out the middle of its frame.
    const height = flight.y[i] - flight.ground[i];
    const clear = height * FOG_FAR;
    const outside = nearestOutside(flight, i, flight.pitch[i], tanV, tanH);
    flight.fogFar[i] = Math.min(clear, outside * HIDDEN_AT);
  }
  erode(flight.fogFar, flight.window);
  blur(flight.fogFar, Math.max(1, Math.round(flight.window / 3)), 3);
  // And never so close that the middle of the frame goes grey. A route that finishes on
  // the region's very edge does show some field beside the gantry — and a field is a
  // better picture than a city lost in fog.
  for (let i = 0; i < n; i++) {
    const height = flight.y[i] - flight.ground[i];
    flight.fogFar[i] = Math.max(flight.fogFar[i], (FOG_FLOOR * height) / Math.sin(flight.pitch[i]));
  }
}

/**
 * Distance to the nearest ground outside the region along the border of the frame, at
 * sample `i` and `pitch`. Infinity is a frame with nothing outside it at all.
 */
export function nearestOutside(flight: Flight, i: number, pitch: number, tanV: number, tanH: number): number {
  const height = flight.y[i] - flight.ground[i];
  const yaw = flight.yaw[i];
  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);
  const cw = Math.cos(yaw);
  const sw = Math.sin(yaw);
  let nearest = Infinity;
  for (const [u, v] of BORDER) {
    // Camera space (u·tanH, v·tanV, -1), pitched down then turned to the heading — the
    // same 'YXZ' order `poseAt` hands to three.
    const ry = v * tanV * cp - sp;
    const rz0 = -v * tanV * sp - cp;
    const rx = u * tanH * cw + rz0 * sw;
    const rz = -u * tanH * sw + rz0 * cw;
    // Above the horizon is sky, and at the haze's distance the sky is the fog's colour.
    if (ry >= -1e-3) continue;
    const t = height / -ry;
    const hx = flight.x[i] + rx * t;
    const hz = flight.z[i] + rz * t;
    if (Math.abs(hx) <= flight.extentX && Math.abs(hz) <= flight.extentZ) continue;
    nearest = Math.min(nearest, t * Math.hypot(rx, ry, rz));
  }
  return nearest;
}

/**
 * The tallest roof within `ROOF_REACH` of a point.
 *
 * Wider than the skyline's own 3×3 look-around, because this camera is not circling a car
 * on a street, it is looking forward and down across a city: a tower a hundred metres off
 * to one side is in the frame, and if it is taller than the camera, the frame is a wall.
 * Clearing everything that close means a tower is only ever seen from above.
 */
function roofNear(skyline: Skyline | null, x: number, z: number): number {
  if (!skyline) return 0;
  let top = skyline.heightAt(x, z);
  for (let k = 0; k < 8; k++) {
    const a = (k / 8) * Math.PI * 2;
    top = Math.max(top, skyline.heightAt(x + Math.sin(a) * ROOF_REACH, z + Math.cos(a) * ROOF_REACH));
  }
  return top;
}

/** Repeated box blur, the window shrinking at the ends so the first and last samples stay put. */
function blur(values: Float32Array, radius: number, passes: number): void {
  const n = values.length;
  if (n < 3 || radius < 1) return;
  const copy = new Float32Array(n);
  for (let pass = 0; pass < passes; pass++) {
    copy.set(values);
    for (let i = 0; i < n; i++) {
      const r = Math.min(radius, i, n - 1 - i);
      let sum = 0;
      for (let k = -r; k <= r; k++) sum += copy[i + k];
      values[i] = sum / (2 * r + 1);
    }
  }
}

/** Running maximum over ±`radius` samples. */
function dilate(values: Float32Array, radius: number): void {
  const n = values.length;
  const copy = values.slice();
  for (let i = 0; i < n; i++) {
    let top = copy[i];
    for (let k = Math.max(0, i - radius); k <= Math.min(n - 1, i + radius); k++) top = Math.max(top, copy[k]);
    values[i] = top;
  }
}

/** Running minimum over ±`radius` samples. */
function erode(values: Float32Array, radius: number): void {
  const n = values.length;
  const copy = values.slice();
  for (let i = 0; i < n; i++) {
    let low = copy[i];
    for (let k = Math.max(0, i - radius); k <= Math.min(n - 1, i + radius); k++) low = Math.min(low, copy[k]);
    values[i] = low;
  }
}
